class SimpleSlider extends HHBaseElement {
  variant = 'single'
  activeIndex = 0

  constructor() {
    super()
    this.scrolledToStart = false
    this.scrolledToEnd = false
    this.classList[!this.isScrollable ? 'add' : 'remove']('not-scrollable')
    this.isScrollable = false
    this.variant = this.getAttribute('variant') || 'single'
    this.firstSlide = this.getAttribute('data-first-slide') || '1'
    this.firstSlideBig = this.getAttribute('data-first-slide-big') || this.firstSlide
    this.$nextBtn = this.querySelector('[data-slider-next]')
    this.$backBtn = this.querySelector('[data-slider-back]')
    this.$status = this.querySelector('[data-slider-status]')
    this.$selectableByControl = this.hasAttribute('data-select-by-control')
    this.$itemsContainer = this.querySelector('[data-slider-items]')
    this.$arrowsContainer = this.querySelector('.simple-carousel-controls')
    this.$dots = this.querySelectorAll('[data-slider-dots] .simple-slider-dot')

    this.$items = Array.from(
      this.querySelectorAll('[data-slider-item]')
    ).filter((item) => !item.classList.contains('hidden'))
    this.tabDisabled = this.getAttribute('data-tab-disabled') || ''

    this.cloneSetCounter = 1

    this._addClones = this._addClones.bind(this)
    this._handleNext = this._handleNext.bind(this)
    this._handleBack = this._handleBack.bind(this)
    this._addToIntersectionObservers =
      this._addToIntersectionObservers.bind(this)
    this._mutationWatcher = this._mutationWatcher.bind(this)
    this._handleContainerScroll = this._handleContainerScroll.bind(this)
    this._handleIntersection = this._handleIntersection.bind(this)
    this._handleADAIntersection = this._handleADAIntersection.bind(this)
    this._initCloneSet = this._initCloneSet.bind(this)
    this._setVisibilityArrows = this._setVisibilityArrows.bind(this)
    this._setActiveIndex = this._setActiveIndex.bind(this)
    this._clearActiveDots = this._clearActiveDots.bind(this)
    this._updateActiveDot = this._updateActiveDot.bind(this)

    this.classList.add(`simple-slider--${this.variant}`)

    if (this.infinite) {
      this.classList.add(`simple-slider--infinite`)
    }
  }

  connectedCallback() {
    if (this.$items.length > 1) {
      this._attachListeners()
      this._applyState()
      this._handleContainerScroll()
      this._initCloneSet()

      if (this.infinite) {
        // Adding Placeholder Clones
        this._addClones()
        this._addClones(false)
      }
    }

    if (this.firstSlide != '1') {
      setTimeout(() => {
        const mql = window.matchMedia('(min-width: 1961px)')
        let distance = this.scrollDistance * (parseFloat(this.firstSlide) - 1)
        if(mql.matches) {
          distance = this.scrollDistance * (parseFloat(this.firstSlideBig) - 1)
        }
        this.scrollCarousel(distance)
      }, 100)
    }

    this._setVisibilityArrows()
  }

  disconnectedCallback() {
    if (this.$items.length > 1) {
      this._removeListeners()
    }
  }

  _setVisibilityArrows() {
    const hasNoScroll =
      this.$itemsContainer.scrollWidth - this.$itemsContainer.clientWidth <= 0
    this.$arrowsContainer?.classList.toggle('hidden', hasNoScroll)
  }

  renderWithBaseZero(amount) {
    return amount < 10 ? `0${amount}` : amount
  }

  get _status() {
    let currentSlide = this.activeIndex + 1

    const finalSlide = this.$items.filter(
      (item) => !item.classList.contains('is-clone')
    ).length

    if (this.$selectableByControl) {
      this.triggerGlobalEvent('simple-slider::activeIndex', {
        activeIndex: this.activeIndex,
      })
      return `${this.renderWithBaseZero(
        currentSlide
      )} / ${this.renderWithBaseZero(finalSlide)}`
    }

    if (this.scrolledToEnd) {
      return `${this.renderWithBaseZero(
        finalSlide
      )} / ${this.renderWithBaseZero(finalSlide)}`
    }

    if (this.scrolledToStart) {
      return `01 / ${this.renderWithBaseZero(finalSlide)}`
    }

    if (this.scrollingForward) {
      currentSlide = this.activeIndex
      if (currentSlide === '00') {
        currentSlide = this.activeIndex + 1
      }
      return `${this.renderWithBaseZero(
        currentSlide
      )} / ${this.renderWithBaseZero(finalSlide)}`
    } else {
      if (currentSlide === finalSlide) {
        currentSlide = this.activeIndex
      }

      return `${this.renderWithBaseZero(
        currentSlide
      )} / ${this.renderWithBaseZero(finalSlide)}`
    }
  }

  get autoplayEnabled() {
    return this.getAttribute('autorotate') === 'true'
  }

  get autoplayInterval() {
    return parseFloat(this.getAttribute('autorotate-interval')) * 1000
  }

  get loops() {
    return !!this.getAttribute('loop')
  }

  get infinite() {
    return this.loops && !this.$status && !this.$selectableByControl
  }

  get scrollDistance() {
    if (this.$items.length > 1) {
      const cssObj = window.getComputedStyle(this.$items[1], null)
      const marginLeft = parseInt(
        cssObj.getPropertyValue('margin-left').replace('px', '')
      )
      return this.$items[1].clientWidth + marginLeft
    } else {
      return this.$items[0].clientWidth
    }
  }

  get carouselInnerPadding() {
    const padding = parseInt(
      getComputedStyle(this.$itemsContainer).getPropertyValue(
        '--simple-carousel-padding'
      )
    )

    return (isNaN(padding) ? 0 : padding) + this.infinite
      ? this.scrollDistance
      : 0
  }

  _setupADA() {
    // Perhaps in the future this could be replaced with role=listbox - more research is required
    if (
      !this.$itemsContainer.classList.contains('product-gallery__mobile-slider')
    ) {
      this.$itemsContainer.setAttribute('role', 'region')
    }

    this.$itemsContainer.setAttribute(
      'aria-label',
      this.getAttribute('aria-label') + ' Items Container' || 'Carousel'
    )
  }

  _initCloneSet() {
    this.$items.forEach((item) => {
      item.classList.add(`clone-set-${this.cloneSetCounter}`)
    })
  }

  _handleButtonEvent(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  _handleNext(event) {
    this._handleButtonEvent(event)

    if (this.scrolledToEnd) {
      if (this.infinite) {
        this._addClones(false)
      } else if (this.loops) {
        if (this.$selectableByControl) {
          const finalSlide =
            this.$items.filter((item) => !item.classList.contains('is-clone'))
              .length - 1
          if (this.activeIndex === finalSlide) {
            this.activeIndex = 0
            this._applyState()
            this._updateActiveDot(0)
            return this.scrollTo(0)
          } else {
            this.activeIndex += 1
            this._applyState()
            return
          }
        } else {
          this._updateActiveDot(0)
          return this.scrollTo(0)
        }
      } else {
        return
      }
    }

    let currentSlideIndex = parseFloat(this.activeIndex + 1)
    this._updateActiveDot(currentSlideIndex)
    this.scrollCarousel(this.scrollDistance)

    if (this.infinite) {
      clearTimeout(this.removeNextClonesTimer)
      this.removeNextClonesTimer = setTimeout(() => {
        this.removeClonesNotInView()
      }, 1000)
    }

    if (this.$selectableByControl) {
      this.activeIndex += 1
      this._applyState()
    }
  }

  _handleBack(event) {
    this._handleButtonEvent(event)

    if (this.scrolledToStart) {
      if (this.infinite) {
        const currentElement = this.$items[0]
        this._addClones(true)
        this.scrollJumpTo(currentElement.offsetLeft)
      } else if (this.loops) {
        const finalSlide =
          this.$items.filter((item) => !item.classList.contains('is-clone'))
            .length - 1
        if (this.activeIndex === 0) {
          this.activeIndex = finalSlide
          this._applyState()
          this._updateActiveDot(finalSlide + 1)
          return this.scrollTo(
            this.$itemsContainer.scrollWidth - this.$itemsContainer.clientWidth
          )
        } else {
          this.activeIndex -= 1
          this._applyState()
          return
        }
      } else {
        return
      }
    }

    let currentSlideIndex = parseFloat(this.activeIndex - 1)
    this._updateActiveDot(currentSlideIndex)
    this.scrollCarousel(-1 * this.scrollDistance)

    if (this.infinite) {
      clearTimeout(this.removePrevClonesTimer)
      this.removePrevClonesTimer = setTimeout(() => {
        this.removeClonesNotInView(true)
      }, 1000)
    }

    if (this.$selectableByControl) {
      this.activeIndex -= 1
      this._applyState()
    }
  }

  _mutationWatcher() {
    this.$items = Array.from(
      this.querySelectorAll('[data-slider-item]')
    ).filter((item) => !item.classList.contains('hidden'))
    this._handleContainerScroll()

    // hides slider arrows if first shown variant has only 1 media
    if (this.$items.length == 1 && !this.$nextBtn.classList.contains('hidden')) {
      this.$nextBtn.classList.add('hidden')
      this.$backBtn?.classList.add('hidden')
    }
  }

  _handleContainerScroll() {
    const { scrollLeft, scrollWidth, clientWidth } = this.$itemsContainer
    this.scrolledToStart = scrollLeft <= this.carouselInnerPadding
    const scrollEndRatio =
      (scrollLeft + this.carouselInnerPadding) / (scrollWidth - clientWidth)
    this.scrolledToEnd = scrollEndRatio > 0.98
    this.isScrollable = scrollWidth > clientWidth
    this._applyState()
  }

  _addClones(prepend = false) {
    if (this.$items.length > 1) {
      const clones = this.$items.map((item) => item.cloneNode(true))
      clones.forEach((item) => {
        item.classList.add('is-clone')
        item.classList.remove(`clone-set-${this.cloneSetCounter}`)
        item.classList.add(
          `clone-set-${
            prepend ? this.cloneSetCounter - 1 : this.cloneSetCounter + 1
          }`
        )
        this._addToIntersectionObservers(item)
      })

      if (prepend) {
        this.$itemsContainer.prepend(...clones)
      } else {
        this.$itemsContainer.append(...clones)
      }

      this.cloneSetCounter = this.cloneSetCounter + 1
      this.triggerEvent('clonesAdded')
  }
}

  _isVisible(elem, removePrevious = false) {
    const scrollLeft = this.$itemsContainer.scrollLeft
    const clientWidth = this.$itemsContainer.clientWidth
    const elementLeft = elem.offsetLeft
    if (removePrevious) {
      const offset = elementLeft - scrollLeft
      const isOutOfView = offset > clientWidth * 2

      return !isOutOfView
    }

    const offset = scrollLeft - elementLeft
    const isOutOfView = offset > clientWidth * 2
    return !isOutOfView
  }

  removeClonesNotInView(removePrevious = false) {
    Array.from(this.querySelectorAll('.is-clone'))
      .filter((item) => !this._isVisible(item, removePrevious))
      .forEach((item) => {
        item.remove()
      })
  }

  _setUpResizeObserver() {
    if ('ResizeObserver' in window) {
      // Update scrollflags when a child's size changes (e.g., a custom element becomes
      // defined or an image is loaded)
      this.resizeObserver = new ResizeObserver(() => {
        this._handleContainerScroll()
        this._setVisibilityArrows()
      })
      Array.from(this.children).forEach((element) => {
        this.resizeObserver.observe(element)
      })
    }
  }

  _addToIntersectionObservers(item) {
    this.activeItemObserver.observe(item)
    this.adaVisiblityObserver.observe(item)
  }

  _setItemADAVisible(item) {
    item.removeAttribute('aria-hidden')
    item.removeAttribute('tabindex')
    item.style.visibility = ''
  }

  _setItemADAHidden(item) {
    item.setAttribute('aria-hidden', 'true')
    item.setAttribute('tabindex', '-1')
    item.style.visibility = 'hidden'
  }

  _handleADAIntersection(entries) {
    entries.forEach((entry) => {
      if (this.tabDisabled === 'true') {
        entry.target.setAttribute('tabindex', '-1')
      } else if (this.tabDisabled === 'false') {
        entry.target.setAttribute('tabindex', '0')
      } else {
        if (entry.isIntersecting) {
          this._setItemADAVisible(entry.target)
        } else {
          this._setItemADAHidden(entry.target)
        }
      }
    })
  }

  _handleIntersection(entries) {
    let gotActiveIndex = false
    entries.forEach((entry) => {
      if (entry.isIntersecting && !gotActiveIndex) {
        const activeIndex = this.$items.indexOf(entry.target)
        if (activeIndex !== this.activeIndex) {
          this.activeIndex = activeIndex
          this._applyState()
        }
        gotActiveIndex = true
      }
    })
  }

  _setActiveIndex(event) {
    this.activeIndex = event.detail.currentIndex
    this._applyState()

    if (this.activeIndex)
      return this.scrollCarousel(this.scrollDistance * this.activeIndex)
    this.scrollTo(0)
  }

  _attachListeners() {
    this.$nextBtn.addEventListener('click', this._handleNext)
    this.$backBtn?.addEventListener('click', this._handleBack)
    this.optionsObserver = new MutationObserver(this._mutationWatcher)
    this.optionsObserver.observe(this.$itemsContainer, {
      childList: true,
    })
    this._setUpResizeObserver()

    this.$itemsContainer.addEventListener('scroll', this._handleContainerScroll)
    this.activeItemObserver = new IntersectionObserver(
      this._handleIntersection,
      { root: this.$itemsContainer, threshold: 0.51 }
    )

    this.adaVisiblityObserver = new IntersectionObserver(
      this._handleADAIntersection,
      { root: this.$itemsContainer, threshold: 0.01 }
    )
    this.$items.forEach((item) => this._addToIntersectionObservers(item))

    if (this.autoplayEnabled) {
      this.setAutoplayTimer()
      this.addEventListener('mouseover', this.clearAutoplayTimer)
      this.addEventListener('mouseout', this.setAutoplayTimer)
    }

    this.addEventListener('swiped-left', this._handleNext, false)
    this.addEventListener('swiped-right', this._handleBack, false)

    document.addEventListener(
      'simple-slider::setActiveIndex',
      this._setActiveIndex
    )
  }

  _removeListeners() {
    this.$nextBtn.removeEventListener('click', this._handleNext)
    this.$backBtn?.removeEventListener('click', this._handleBack)
    this.$itemsContainer.removeEventListener('scroll', this._applyIndexState)
    this.$itemsContainer.removeEventListener(
      'scroll',
      this._handleContainerScroll
    )

    if (this.optionsObserver) this.optionsObserver.disconnect()
    if (this.activeItemObserver) this.activeItemObserver.disconnect()
    if (this.adaVisiblityObserver) this.adaVisiblityObserver.disconnect()
    if (this.resizeObserver) this.resizeObserver.disconnect()

    if (this.autoplayEnabled) {
      this.removeEventListener('mouseenter', this.clearAutoplayTimer)
      this.removeEventListener('mouseout', this.setAutoplayTimer)
    }

    this.removeEventListener('swiped-left', this._handleNext, false)
    this.removeEventListener('swiped-right', this._handleBack, false)

    document.removeEventListener(
      'simple-slider::setActiveIndex',
      this._setActiveIndex
    )
  }

  _applyState() {
    // check slide status
    let atEndOfSlides = false

    if (this._status && !this.infinite) {
      let numbers = this._status.split('/')
      if (parseFloat(numbers[0]) === parseFloat(numbers[1])) {
        atEndOfSlides = true
      }
    }

    if (this.$backBtn) {
      this.$backBtn.disabled = this.scrolledToStart;
    }

    const requiresActiveEndState = this.$nextBtn?.hasAttribute('data-slider-next-step');

    if (this.$nextBtn && !requiresActiveEndState) {
      this.$nextBtn.disabled = this.scrolledToEnd || atEndOfSlides;
    }

    this.classList[this.scrolledToStart ? 'add' : 'remove']('first-slide')
    this.classList[this.scrolledToEnd || atEndOfSlides ? 'add' : 'remove'](
      'last-slide'
    )
    this.classList[!this.isScrollable ? 'add' : 'remove']('not-scrollable')
    if (this.$status) {
      clearTimeout(this.$setStatusTimer)
      this.$setStatusTimer = setTimeout(() => {
        this.$status.innerText = this._status
      }, 300)
    }

    if (!requiresActiveEndState) {
      this.$backBtn?.[this.scrolledToStart ? 'setAttribute' : 'removeAttribute']('disabled', true)
      this.$nextBtn?.[this.scrolledToEnd ? 'setAttribute' : 'removeAttribute']('disabled', true)
    }
  }

  clearAutoplayTimer() {
    try {
      clearInterval(this.slideshowTimer)
      this.slideshowTimer = null
    } catch (e) {
      console.log(e)
    }
  }

  setAutoplayTimer() {
    try {
      let interval = this.autoplayInterval
      this.slideshowTimer = setInterval(() => {
        this._handleNext()
      }, interval)
    } catch (error) {
      console.error(error)
    }
  }

  scrollCarousel(num) {
    this.$itemsContainer.scrollLeft += num
  }

  scrollTo(num) {
    this.$itemsContainer.scrollLeft = num
  }

  scrollJumpTo(x) {
    this.$itemsContainer.style.scrollBehavior = 'unset'
    this.$itemsContainer.scrollTo(x, 0)
    this.$itemsContainer.style.scrollBehavior = ''
  }

  static get observedAttributes() {
    return ['variant']
  }

  _clearClones() {
    let clones = [...document.querySelectorAll('.is-clone')]
    clones.forEach((clone) => clone.remove())
  }

  _clearActiveDots() {
    let dots = [...this.$dots]
    dots.map((dot) => dot.classList.remove('active'))
  }

  _updateActiveDot(newDotIndex) {
    this._clearActiveDots()
    this.$dots[newDotIndex]?.classList.add('active')
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Product Gallery hides/shows respective variants

    if (name === 'variant') {
      // clear clones
      this._clearClones()
      this.$items = Array.from(
        this.querySelectorAll('[data-slider-item]')
      ).filter((item) => !item.classList.contains('hidden'))

      this.scrollJumpTo(0)
    }
  }
}
window.customElements.define('simple-slider', SimpleSlider)
